<script setup lang="ts">
import { ref } from 'vue'
import ServiceModal from '../components/ServiceModal.vue'
import CornerDecoration from '../components/CornerDecoration.vue'
import { services } from '../data/services'

const props = defineProps<{ isMobile: boolean }>()

const activeAccordion = ref<number | null>(null)
const activeService = ref<{ title: string; items: string[] } | null>(null)
const showServiceModal = ref(false)

const toggleAccordion = (index: number) => {
  if (props.isMobile) {
    activeService.value = services[index]
    showServiceModal.value = true
  } else {
    activeAccordion.value = activeAccordion.value === index ? null : index
  }
}

const startTransition = (el: Element, done: () => void) => {
  const htmlEl = el as HTMLElement
  htmlEl.style.maxHeight = '0'
  void htmlEl.offsetHeight
  htmlEl.style.maxHeight = `${htmlEl.scrollHeight}px`
  htmlEl.addEventListener('transitionend', done, { once: true })
}

const endTransition = (el: Element, done: () => void) => {
  const htmlEl = el as HTMLElement
  htmlEl.style.maxHeight = `${htmlEl.scrollHeight}px`
  void htmlEl.offsetHeight
  htmlEl.style.maxHeight = '0'
  htmlEl.addEventListener('transitionend', done, { once: true })
}
</script>

<template>
  <section
    id="services"
    class="bg-[#FF5A00] flex flex-col items-center justify-center text-black w-full min-h-[85vh] relative z-10"
  >
    <ServiceModal
      :show="showServiceModal"
      :title="activeService?.title || ''"
      :items="activeService?.items || []"
      @close="showServiceModal = false"
    />

    <div class="max-w-[850px] px-4 pb-8 pt-12 md:pt-[80px] md:px-[124px] md:pb-[80px]">
      <div class="relative pt-[2px] pb-10">
        <CornerDecoration color="black" offset="-8px" />

      <div class="mb-8">
        <h2 class="uppercase text-xs opacity-50 mb-4 mt-4 md:mt-8">About</h2>
        <p class="text-base md:text-xl leading-[24px]">
          Deep Cuts is a creative studio, bridging the gap between sight and sound. 
        </p>
      </div>

      <h2 class="uppercase text-xs opacity-50 mb-4">Services</h2>
      <div class="space-y-0">
        <div v-for="(item, index) in services" :key="index" class="border-t border-black">
          <button
            @click="toggleAccordion(index)"
            class="w-full py-2 flex justify-between items-center text-left text-base md:text-[20px]"
          >
            <span>{{ item.title }}</span>
            <span
              class="text-[1rem] transition-transform duration-300"
              :class="{ 'rotate-45': activeAccordion === index }"
              >➕</span
            >
          </button>
          <transition name="accordion" @enter="startTransition" @leave="endTransition">
            <div
              v-if="activeAccordion === index"
              class="overflow-hidden transition-all duration-300 ease-in-out"
            >
              <ul class="pb-4 opacity-70 text-base md:text-[20px] leading-[24px]">
                <li v-for="(subItem, i) in item.items" :key="i">{{ subItem }}</li>
              </ul>
            </div>
          </transition>
        </div>
        <div class="border-t border-black"></div>
      </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 200px;
  opacity: 1;
}
</style>
